import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axiosInstance from '../lib/axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ShoppingCart, Star, Truck, Shield, Check } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { formatPrice } from '../lib/utils';
import LoadingSpinner from '../components/LoadingSpinner';

export default function GaneshBakeryMacaroonPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchProducts();
    // Set page-specific SEO
    document.title = 'Ganesh Bakery Macaroon Online | Buy Thoothukudi Macaroon - NammaHut';
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axiosInstance.get(`${API_URL}/api/products?search=macaroon`);
      setProducts(response.data.products || response.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    addToCart({
      id: product.id || product._id,
      name: product.name,
      price: product.sale_price || product.price,
      image: product.images?.[0],
      quantity: 1,
      vendor_id: product.vendor_id
    });
  };

  return (
    <>
      <Helmet>
        <title>Buy Ganesh Bakery Macaroon Online | Thoothukudi Macaroon ₹399 - NammaHut</title>
        <meta name="description" content="Buy original Ganesh Bakery Macaroon online ₹399. Famous Thoothukudi Macaroon (Tuticorin Macaroon) from Ganesh Bakery delivered fresh across India. ⭐4.8 rating, 1000+ happy customers. Free shipping on orders above ₹499!" />
        <meta name="keywords" content="ganesh bakery macaroon, ganesh bakery macaroon online, thoothukudi macaroon, tuticorin macaroon, ganesh bakery thoothukudi, buy ganesh bakery macaroon, ganesh bakery macaroon price, ganesh bakery macaroon home delivery" />
        <link rel="canonical" href="https://nammahut.com/ganesh-bakery-macaroon" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Buy Ganesh Bakery Macaroon Online ₹399 | NammaHut" />
        <meta property="og:description" content="Original Ganesh Bakery Thoothukudi Macaroon. ⭐4.8 rating. Free delivery across India!" />
        <meta property="og:url" content="https://nammahut.com/ganesh-bakery-macaroon" />
        <meta property="og:type" content="product" />
        <meta property="og:image" content="https://nammahut.com/og-ganesh-macaroon.jpg" />
        <meta property="product:price:amount" content="399" />
        <meta property="product:price:currency" content="INR" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Buy Ganesh Bakery Macaroon Online ₹399" />
        <meta name="twitter:description" content="Original Thoothukudi Macaroon from Ganesh Bakery. Free delivery!" />
        
        {/* Product Schema with AggregateRating for rich snippets */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "Ganesh Bakery Macaroon - Thoothukudi",
            "description": "Original Ganesh Bakery Thoothukudi Macaroon, the famous crispy Indian macaroon made with egg whites, sugar and cashews. Fresh from Thoothukudi (Tuticorin), Tamil Nadu.",
            "image": [
              "https://nammahut.com/uploads/ganesh-bakery-macaroon-1.jpg",
              "https://nammahut.com/uploads/ganesh-bakery-macaroon-2.jpg"
            ],
            "brand": {
              "@type": "Brand",
              "name": "Ganesh Bakery"
            },
            "sku": "GB-MACAROON-250G",
            "mpn": "NH-GBMAC250",
            "offers": {
              "@type": "AggregateOffer",
              "url": "https://nammahut.com/ganesh-bakery-macaroon",
              "priceCurrency": "INR",
              "lowPrice": "399",
              "highPrice": "999",
              "offerCount": products.length || 5,
              "availability": "https://schema.org/InStock",
              "seller": {
                "@type": "Organization",
                "name": "NammaHut"
              }
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "reviewCount": "1247",
              "bestRating": "5",
              "worstRating": "1"
            },
            "review": [
              {
                "@type": "Review",
                "reviewRating": {
                  "@type": "Rating",
                  "ratingValue": "5",
                  "bestRating": "5"
                },
                "author": {
                  "@type": "Person",
                  "name": "Priya S."
                },
                "reviewBody": "Authentic Ganesh Bakery macaroon! Tastes exactly like the one I had in Thoothukudi. Fast delivery and fresh product."
              },
              {
                "@type": "Review",
                "reviewRating": {
                  "@type": "Rating",
                  "ratingValue": "5",
                  "bestRating": "5"
                },
                "author": {
                  "@type": "Person",
                  "name": "Ravi K."
                },
                "reviewBody": "Best macaroon online. My family loved it. Will order again!"
              }
            ]
          })}
        </script>
        
        {/* BreadcrumbList Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://nammahut.com"},
              {"@type": "ListItem", "position": 2, "name": "Products", "item": "https://nammahut.com/products"},
              {"@type": "ListItem", "position": 3, "name": "Ganesh Bakery Macaroon", "item": "https://nammahut.com/ganesh-bakery-macaroon"}
            ]
          })}
        </script>
        
        {/* FAQPage Schema for FAQ rich snippets */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What is Ganesh Bakery Macaroon?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ganesh Bakery Macaroon is a traditional Indian biscuit from Thoothukudi, Tamil Nadu. Made with egg whites, sugar, and cashews, it's known for its light, crispy texture that melts in your mouth."
                }
              },
              {
                "@type": "Question",
                "name": "How to buy Ganesh Bakery Macaroon online?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "You can buy authentic Ganesh Bakery Macaroon online at NammaHut. We deliver fresh macaroons across India within 3-7 business days. Free shipping on orders above ₹499."
                }
              },
              {
                "@type": "Question",
                "name": "What is the price of Ganesh Bakery Macaroon?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ganesh Bakery Macaroon starts at ₹399 for 250g pack. We offer various pack sizes and combo deals. Check our website for current prices and offers."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-amber-50 to-orange-50 py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="text-sm mb-6">
              <ol className="flex items-center space-x-2">
                <li><Link to="/" className="text-green-600 hover:underline">Home</Link></li>
                <li><span className="text-gray-400">/</span></li>
                <li><Link to="/products" className="text-green-600 hover:underline">Products</Link></li>
                <li><span className="text-gray-400">/</span></li>
                <li className="text-gray-600">Ganesh Bakery Macaroon</li>
              </ol>
            </nav>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Ganesh Bakery Macaroon - Thoothukudi's Finest
            </h1>
            <p className="text-lg md:text-xl text-gray-700 max-w-3xl">
              Experience the authentic taste of Thoothukudi Macaroon from the legendary Ganesh Bakery. 
              Fresh, crispy, and delivered to your doorstep across India.
            </p>
          </div>
        </section>

        {/* SEO Content Section */}
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-lg max-w-4xl">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                About Ganesh Bakery Thoothukudi Macaroon
              </h2>
              <p className="text-gray-700 mb-4">
                <strong>Ganesh Bakery Macaroon</strong>, also known as <strong>Thoothukudi Macaroon</strong> or 
                <strong> Tuticorin Macaroon</strong>, is a beloved traditional Indian delicacy that has been 
                delighting taste buds for generations. Unlike the French macaron, the Indian macaroon is a 
                crispy, airy biscuit made with egg whites, sugar, and cashews - creating a unique texture 
                that melts in your mouth.
              </p>
              <p className="text-gray-700 mb-4">
                Originating from the coastal town of <strong>Thoothukudi (Tuticorin)</strong> in Tamil Nadu, 
                these macaroons have become synonymous with the region's culinary heritage. Ganesh Bakery 
                has perfected this recipe over decades, making their macaroons the most sought-after in India.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                Why Buy Ganesh Bakery Macaroon from NammaHut?
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                  <span><strong>100% Authentic</strong> - Directly sourced from Ganesh Bakery, Thoothukudi</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                  <span><strong>Fresh Delivery</strong> - Carefully packed to maintain crispness</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                  <span><strong>Pan-India Shipping</strong> - Delivered to your doorstep anywhere in India</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                  <span><strong>Best Prices</strong> - Competitive pricing with no compromise on quality</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Products Section */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">
              Shop Ganesh Bakery Macaroon
            </h2>
            
            {loading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner size="lg" />
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600 mb-4">No macaroon products found. Check out our other products!</p>
                <Link to="/products">
                  <Button>Browse All Products</Button>
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {products.map((product) => (
                  <Card key={product.id || product._id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <Link to={`/products/${product.slug || product.id || product._id}`}>
                      <div className="aspect-square bg-gray-100 relative">
                        <img
                          src={product.images?.[0] || 'https://via.placeholder.com/300?text=Macaroon'}
                          alt={`${product.name} - Ganesh Bakery Thoothukudi Macaroon`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        {product.sale_price && product.sale_price < product.price && (
                          <Badge className="absolute top-2 left-2 bg-red-500">
                            {Math.round((1 - product.sale_price / product.price) * 100)}% OFF
                          </Badge>
                        )}
                      </div>
                    </Link>
                    <CardContent className="p-4">
                      <Link to={`/products/${product.slug || product.id || product._id}`}>
                        <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 hover:text-green-600">
                          {product.name}
                        </h3>
                      </Link>
                      <div className="flex items-center gap-1 mb-2">
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        <span className="text-sm text-gray-600">4.5 (24 reviews)</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-xl font-bold text-gray-900">
                            {formatPrice(product.sale_price || product.price)}
                          </span>
                          {product.sale_price && product.sale_price < product.price && (
                            <span className="text-sm text-gray-500 line-through ml-2">
                              {formatPrice(product.price)}
                            </span>
                          )}
                        </div>
                        <Button 
                          size="sm" 
                          onClick={() => handleAddToCart(product)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <ShoppingCart className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Trust Signals */}
        <section className="py-12 bg-white border-t">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-full">
                  <Truck className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Free Shipping</h3>
                  <p className="text-sm text-gray-600">On orders above ₹499</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <Shield className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">100% Authentic</h3>
                  <p className="text-sm text-gray-600">Genuine products guaranteed</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-100 rounded-full">
                  <Star className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Top Rated</h3>
                  <p className="text-sm text-gray-600">4.8★ from 1000+ customers</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section for SEO */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  What is Ganesh Bakery Macaroon?
                </h3>
                <p className="text-gray-700">
                  Ganesh Bakery Macaroon is a traditional Indian biscuit from Thoothukudi, Tamil Nadu. 
                  Made with egg whites, sugar, and cashews, it's known for its light, crispy texture.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  How long does delivery take?
                </h3>
                <p className="text-gray-700">
                  We deliver across India within 3-7 business days. Express delivery is available 
                  in select cities for faster delivery.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  Is this the original Ganesh Bakery product?
                </h3>
                <p className="text-gray-700">
                  Yes! We source directly from verified vendors in Thoothukudi to ensure you receive 
                  100% authentic Ganesh Bakery Macaroons.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
